class BlogFilters {
  constructor() {
    this.form = document.getElementById('blog-filter-form');
    this.categoryFilter = document.getElementById('category-filter');
    this.tagFilter = document.getElementById('tag-filter');
    this.clearButton = document.getElementById('clear-filters');
    this.articlesContainer = document.getElementById('blog-articles-container');
    
    if (!this.form || !this.articlesContainer) return;
    
    this.blogHandle = this.articlesContainer.dataset.blogHandle;
    this.isLoading = false;
    this.debounceTimer = null;
    this.selectedCategories = new Set();
    this.selectedTags = new Set();
    
    this.bindEvents();
    this.initializeFromURL();
  }

  bindEvents() {
    // Handle checkbox group (multi select) - new dropdown design
    const categoryCheckboxes = this.form.querySelectorAll('#category-filter input[type="checkbox"]');
    const tagCheckboxes = this.form.querySelectorAll('#tag-filter input[type="checkbox"]');
    
    categoryCheckboxes.forEach(checkbox => {
      checkbox.addEventListener('change', this.handleCategoryCheckboxChange.bind(this));
    });
    
    tagCheckboxes.forEach(checkbox => {
      checkbox.addEventListener('change', this.handleTagCheckboxChange.bind(this));
    });
    
    // Initialize Bootstrap dropdowns if available
    this.initializeBootstrapDropdowns();
    
    // Setup click-outside handler (works for both Bootstrap and manual modes)
    this.setupClickOutsideHandler();
    
    // Prevent dropdown from closing when clicking inside checkbox area
    const dropdownMenus = this.form.querySelectorAll('.dropdown-menu');
    dropdownMenus.forEach(menu => {
      menu.addEventListener('click', (e) => {
        // Only prevent default if clicking on checkbox or label
        if (e.target.matches('input[type="checkbox"], .form-check-label, .form-check')) {
          e.stopPropagation();
        }
      });
    });
    
    // Clear filters button
    if (this.clearButton) {
      this.clearButton.addEventListener('click', this.clearFilters.bind(this));
    }
  }

  initializeBootstrapDropdowns() {
    // Initialize Bootstrap dropdowns if Bootstrap is available
    if (typeof bootstrap !== 'undefined' && bootstrap.Dropdown) {
      const dropdownElements = this.form.querySelectorAll('[data-bs-toggle="dropdown"]');
      dropdownElements.forEach(element => {
        new bootstrap.Dropdown(element);
        
        // Add event listeners for arrow icon changes
        this.setupArrowIconToggle(element);
      });
    } else {
      // Fallback: manual dropdown toggle if Bootstrap is not available
      const dropdownButtons = this.form.querySelectorAll('[data-bs-toggle="dropdown"]');
      dropdownButtons.forEach(button => {
        // Setup arrow icon toggle for manual dropdowns
        this.setupArrowIconToggle(button);
        
        button.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation(); // Prevent event from bubbling to document
          
          const dropdownMenu = button.nextElementSibling;
          if (dropdownMenu) {
            const isOpen = dropdownMenu.classList.contains('show');
            
            // Close all other dropdowns and reset their arrows
            this.closeAllDropdowns();
            
            // Toggle current dropdown
            if (!isOpen) {
              dropdownMenu.classList.add('show');
              button.classList.add('show');
              button.setAttribute('aria-expanded', 'true');
              // Change arrow to up
              const arrow = button.querySelector('svg use');
              if (arrow) {
                arrow.setAttribute('href', '#icon-arrow-up');
              }
            }
          }
        });
      });
    }
  }

  /**
   * Close all open dropdowns and reset their state
   */
  closeAllDropdowns() {
    this.form.querySelectorAll('.dropdown-menu.show').forEach(menu => {
      menu.classList.remove('show');
    });
    this.form.querySelectorAll('.dropdown-toggle.show').forEach(btn => {
      btn.classList.remove('show');
      btn.setAttribute('aria-expanded', 'false');
      // Reset arrow icon
      const arrow = btn.querySelector('svg use');
      if (arrow) {
        arrow.setAttribute('href', '#icon-arrow-down');
      }
    });
  }

  /**
   * Setup click outside handler to close dropdowns
   * Works for both Bootstrap and manual dropdown implementations
   */
  setupClickOutsideHandler() {
    document.addEventListener('click', (e) => {
      // Check if click is outside the form
      if (!this.form) return;
      
      // If clicking outside the form and its descendants
      if (!this.form.contains(e.target)) {
        // Bootstrap dropdowns
        if (typeof bootstrap !== 'undefined' && bootstrap.Dropdown) {
          const dropdownButtons = this.form.querySelectorAll('[data-bs-toggle="dropdown"]');
          dropdownButtons.forEach(button => {
            const bsDropdown = bootstrap.Dropdown.getInstance(button);
            if (bsDropdown) {
              bsDropdown.hide();
            }
          });
        }
        
        // Manual dropdowns (fallback) or if Bootstrap hide didn't work
        this.closeAllDropdowns();
      }
    });
    
    // Also handle Escape key to close dropdowns
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' || e.keyCode === 27) {
        // Bootstrap dropdowns
        if (typeof bootstrap !== 'undefined' && bootstrap.Dropdown) {
          const dropdownButtons = this.form.querySelectorAll('[data-bs-toggle="dropdown"]');
          dropdownButtons.forEach(button => {
            const bsDropdown = bootstrap.Dropdown.getInstance(button);
            if (bsDropdown) {
              bsDropdown.hide();
            }
          });
        }
        
        // Manual dropdowns (fallback)
        this.closeAllDropdowns();
      }
    });
  }

  /**
   * Setup arrow icon toggle for dropdown buttons
   * Changes arrow-down to arrow-up when dropdown opens
   * @param {HTMLElement} dropdownButton - The dropdown button element
   */
  setupArrowIconToggle(dropdownButton) {
    const arrow = dropdownButton.querySelector('svg use');
    if (!arrow) return;

    // For Bootstrap dropdowns
    if (typeof bootstrap !== 'undefined' && bootstrap.Dropdown) {
      // Listen for Bootstrap dropdown events
      dropdownButton.addEventListener('show.bs.dropdown', () => {
        arrow.setAttribute('href', '#icon-arrow-up');
      });

      dropdownButton.addEventListener('hide.bs.dropdown', () => {
        arrow.setAttribute('href', '#icon-arrow-down');
      });
    }
    // Manual dropdown toggle is handled in the click handler above
  }

  initializeFromURL() {
    const urlParams = new URLSearchParams(window.location.search);
    const currentTags = this.getCurrentTagsFromURL();
    
    // Separate categories and tags
    const categories = currentTags.filter(tag => tag.startsWith('cat_'));
    const tags = currentTags.filter(tag => tag.startsWith('tag_'));
    
    // Set dropdown values based on current URL tags (for single select dropdowns)
    if (this.categoryFilter && this.categoryFilter.tagName === 'SELECT' && categories.length > 0) {
      this.categoryFilter.value = categories[0]; // Use first category for dropdown
    }
    if (this.tagFilter && this.tagFilter.tagName === 'SELECT' && tags.length > 0) {
      this.tagFilter.value = tags[0]; // Use first tag for dropdown
    }
    
    // Set checkbox states (for multi select checkboxes)
    if (categories.length > 0) {
      this.selectedCategories = new Set(categories);
      const categoryCheckboxes = this.form.querySelectorAll('#category-filter input[type="checkbox"]');
      categoryCheckboxes.forEach(checkbox => {
        checkbox.checked = categories.includes(checkbox.value);
      });
      
      // Update dropdown button text to show count
      this.updateDropdownButtonText('category');
    }
    
    if (tags.length > 0) {
      this.selectedTags = new Set(tags);
      const tagCheckboxes = this.form.querySelectorAll('#tag-filter input[type="checkbox"]');
      tagCheckboxes.forEach(checkbox => {
        checkbox.checked = tags.includes(checkbox.value);
      });
      
      // Update dropdown button text to show count
      this.updateDropdownButtonText('tag');
    }
  }

  getCurrentTagsFromURL() {
    const path = window.location.pathname;
    const taggedMatch = path.match(/\/tagged\/([^\/]+)/);
    if (taggedMatch) {
      return taggedMatch[1].split('+');
    }
    return [];
  }

  handleCategoryCheckboxChange(event) {
    const checkbox = event.target;
    const categoryValue = checkbox.value;
    
    if (checkbox.checked) {
      this.selectedCategories.add(categoryValue);
    } else {
      this.selectedCategories.delete(categoryValue);
    }
    
    this.updateDropdownButtonText('category');
    console.log('Category selection changed:', Array.from(this.selectedCategories));
    this.handleMultiSelectFilterChange();
  }

  handleTagCheckboxChange(event) {
    const checkbox = event.target;
    const tagValue = checkbox.value;
    
    if (checkbox.checked) {
      this.selectedTags.add(tagValue);
    } else {
      this.selectedTags.delete(tagValue);
    }
    
    this.updateDropdownButtonText('tag');
    console.log('Tag selection changed:', Array.from(this.selectedTags));
    this.handleMultiSelectFilterChange();
  }

  handleFilterChange() {
    if (this.isLoading) return;
    
    // Clear existing debounce timer
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }
    
    // Debounce the filter change to prevent rapid requests
    this.debounceTimer = setTimeout(() => {
      const selectedCategory = this.categoryFilter ? this.categoryFilter.value : '';
      const selectedTag = this.tagFilter ? this.tagFilter.value : '';
      
      this.filterArticles(selectedCategory, selectedTag);
    }, 300);
  }

  handleMultiSelectFilterChange() {
    if (this.isLoading) return;
    
    // Clear existing debounce timer
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }
    
    // Debounce the filter change to prevent rapid requests
    this.debounceTimer = setTimeout(() => {
      const selectedCategories = Array.from(this.selectedCategories);
      const selectedTags = Array.from(this.selectedTags);
      
      this.filterArticlesMultiple(selectedCategories, selectedTags);
    }, 300);
  }

  updateDropdownButtonText(filterType) {
    let button, selectedSet, defaultText;
    
    if (filterType === 'category') {
      button = document.getElementById('categoryDropdown');
      selectedSet = this.selectedCategories;
      defaultText = 'CATEGORIES';
    } else if (filterType === 'tag') {
      button = document.getElementById('tagDropdown');
      selectedSet = this.selectedTags;
      defaultText = 'TYPES';
    }
    
    if (!button) return;
    
    const labelSpan = button.querySelector('.filter-label');
    const countSpan = button.querySelector('.selected-count');
    
    // Smooth transition for count appearance/disappearance
    if (selectedSet.size === 0) {
      // No selections - hide count with animation
      countSpan.style.display = 'none';
      labelSpan.textContent = defaultText;
      
      // Remove active state from button
      button.classList.remove('btn-primary');
      button.classList.add('btn-outline-secondary');
    } else {
      // Show count of selections with animation
      labelSpan.textContent = defaultText;
      countSpan.textContent = `(${selectedSet.size})`;
      countSpan.style.display = 'inline';
      
      // Add active state to button
      button.classList.remove('btn-outline-secondary');
      button.classList.add('btn-primary');
    }
  }

  clearFilters() {
    // Clear debounce timer
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }
    
    // Clear dropdown filters
    if (this.categoryFilter && this.categoryFilter.tagName === 'SELECT') {
      this.categoryFilter.value = '';
    }
    if (this.tagFilter && this.tagFilter.tagName === 'SELECT') {
      this.tagFilter.value = '';
    }
    
    // Clear checkbox filters
    const categoryCheckboxes = this.form.querySelectorAll('#category-filter input[type="checkbox"]');
    const tagCheckboxes = this.form.querySelectorAll('#tag-filter input[type="checkbox"]');
    
    categoryCheckboxes.forEach(checkbox => checkbox.checked = false);
    tagCheckboxes.forEach(checkbox => checkbox.checked = false);
    
    // Clear selected sets
    this.selectedCategories.clear();
    this.selectedTags.clear();
    
    // Update dropdown button texts
    this.updateDropdownButtonText('category');
    this.updateDropdownButtonText('tag');
    
    this.filterArticles('', '');
  }

  async filterArticles(category, tag) {
    if (this.isLoading) return;
    
    this.isLoading = true;
    this.showLoadingState();
    
    try {
      // Build the URL for filtered results
      let url = `/blogs/${this.blogHandle}`;
      const tags = [];
      
      if (category) tags.push(category);
      if (tag) tags.push(tag);
      
      if (tags.length > 0) {
        url += `/tagged/${tags.join('+')}`;
      }
      
      // Get the section ID from the blog articles container's parent section
      const blogSection = this.articlesContainer.closest('[data-section-id]');
      let finalSectionId = null;
      
      if (blogSection) {
        finalSectionId = blogSection.dataset.sectionId;
      } else {
        // Fallback: try to find any section that contains the blog template
        const allSections = document.querySelectorAll('[data-section-id]');
        
        for (const section of allSections) {
          if (section.contains(this.articlesContainer)) {
            finalSectionId = section.dataset.sectionId;
            break;
          }
        }
      }
      
      if (!finalSectionId) {
        throw new Error('Could not find section ID for blog template');
      }
      
      const fetchUrl = url + `?sections=${finalSectionId}`;
      
      // Add timeout to fetch request
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout
      
      const response = await fetch(fetchUrl, {
        signal: controller.signal,
        headers: {
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        }
      });
      
      clearTimeout(timeoutId);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch filtered results: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data[finalSectionId]) {
        this.updateArticlesDisplay(data[finalSectionId], category, tag);
        this.updateURL(category, tag);
      } else {
        throw new Error('Section data not found in response');
      }
      
    } catch (error) {
      // Handle specific error types
      if (error.name === 'AbortError') {
        // Request was aborted (timeout)
        this.handleError(new Error('Request timed out. Please check your connection and try again.'));
      } else {
        this.handleError(error);
      }
    } finally {
      this.isLoading = false;
      this.hideLoadingState();
    }
  }

  async filterArticlesMultiple(categories, tags) {
    if (this.isLoading) return;
    
    this.isLoading = true;
    this.showLoadingState();
    
    try {
      // Build the URL for filtered results with multiple selections
      let url = `/blogs/${this.blogHandle}`;
      const allTags = [...categories, ...tags];
      
      if (allTags.length > 0) {
        url += `/tagged/${allTags.join('+')}`;
      }
      
      // Get the section ID from the blog articles container's parent section
      const blogSection = this.articlesContainer.closest('[data-section-id]');
      let finalSectionId = null;
      
      if (blogSection) {
        finalSectionId = blogSection.dataset.sectionId;
      } else {
        // Fallback: try to find any section that contains the blog template
        const allSections = document.querySelectorAll('[data-section-id]');
        
        for (const section of allSections) {
          if (section.contains(this.articlesContainer)) {
            finalSectionId = section.dataset.sectionId;
            break;
          }
        }
      }
      
      if (!finalSectionId) {
        throw new Error('Could not find section ID for blog template');
      }
      
      const fetchUrl = url + `?sections=${finalSectionId}`;
      
      // Add timeout to fetch request
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout
      
      const response = await fetch(fetchUrl, {
        signal: controller.signal,
        headers: {
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        }
      });
      
      clearTimeout(timeoutId);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch filtered results: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data[finalSectionId]) {
        this.updateArticlesDisplayMultiple(data[finalSectionId], categories, tags);
        this.updateURLMultiple(categories, tags);
      } else {
        throw new Error('Section data not found in response');
      }
      
    } catch (error) {
      // Handle specific error types
      if (error.name === 'AbortError') {
        // Request was aborted (timeout)
        this.handleError(new Error('Request timed out. Please check your connection and try again.'));
      } else {
        this.handleError(error);
      }
    } finally {
      this.isLoading = false;
      this.hideLoadingState();
    }
  }

  updateArticlesDisplay(htmlContent, category, tag) {
    const parser = new DOMParser();
    const newDoc = parser.parseFromString(htmlContent, 'text/html');
    const newContainer = newDoc.getElementById('blog-articles-container');
    
    if (newContainer) {
      this.articlesContainer.innerHTML = newContainer.innerHTML;
      this.reinitializeLazyLoading();
    } else {
      // Fallback: try to find the entire section content
      const sectionContent = newDoc.querySelector('[data-section-id]');
      if (sectionContent) {
        const articlesContainer = sectionContent.querySelector('#blog-articles-container');
        if (articlesContainer) {
          this.articlesContainer.innerHTML = articlesContainer.innerHTML;
          this.reinitializeLazyLoading();
        } else {
          throw new Error('Could not find blog articles container in response');
        }
      } else {
        throw new Error('Could not find section content in response');
      }
    }
    
    // Update filter dropdown states
    if (this.categoryFilter && category !== this.categoryFilter.value) {
      this.categoryFilter.value = category;
    }
    if (this.tagFilter && tag !== this.tagFilter.value) {
      this.tagFilter.value = tag;
    }
  }

  updateArticlesDisplayMultiple(htmlContent, categories, tags) {
    const parser = new DOMParser();
    const newDoc = parser.parseFromString(htmlContent, 'text/html');
    const newContainer = newDoc.getElementById('blog-articles-container');
    
    if (newContainer) {
      this.articlesContainer.innerHTML = newContainer.innerHTML;
      this.reinitializeLazyLoading();
    } else {
      // Fallback: try to find the entire section content
      const sectionContent = newDoc.querySelector('[data-section-id]');
      if (sectionContent) {
        const articlesContainer = sectionContent.querySelector('#blog-articles-container');
        if (articlesContainer) {
          this.articlesContainer.innerHTML = articlesContainer.innerHTML;
          this.reinitializeLazyLoading();
        } else {
          throw new Error('Could not find blog articles container in response');
        }
      } else {
        throw new Error('Could not find section content in response');
      }
    }
    
    // Update filter states for checkboxes
    this.updateCheckboxStates(categories, tags);
  }

  updateCheckboxStates(categories, tags) {
    // Update category checkboxes
    const categoryCheckboxes = this.form.querySelectorAll('#category-filter input[type="checkbox"]');
    categoryCheckboxes.forEach(checkbox => {
      checkbox.checked = categories.includes(checkbox.value);
    });
    
    // Update tag checkboxes
    const tagCheckboxes = this.form.querySelectorAll('#tag-filter input[type="checkbox"]');
    tagCheckboxes.forEach(checkbox => {
      checkbox.checked = tags.includes(checkbox.value);
    });
    
    // Update selected sets
    this.selectedCategories = new Set(categories);
    this.selectedTags = new Set(tags);
    
    // Update dropdown button texts
    this.updateDropdownButtonText('category');
    this.updateDropdownButtonText('tag');
  }

  updateURL(category, tag) {
    let url = `/blogs/${this.blogHandle}`;
    const tags = [];
    
    if (category) tags.push(category);
    if (tag) tags.push(tag);
    
    if (tags.length > 0) {
      url += `/tagged/${tags.join('+')}`;
    }
    
    // Update browser history without page reload
    window.history.pushState(
      { category, tag }, 
      '', 
      url
    );
  }

  updateURLMultiple(categories, tags) {
    let url = `/blogs/${this.blogHandle}`;
    const allTags = [...categories, ...tags];
    
    if (allTags.length > 0) {
      url += `/tagged/${allTags.join('+')}`;
    }
    
    // Update browser history without page reload
    window.history.pushState(
      { categories, tags }, 
      '', 
      url
    );
  }

  showLoadingState() {
    this.articlesContainer.style.opacity = '0.6';
    this.articlesContainer.style.pointerEvents = 'none';
    
    // Add loading indicator
    const loadingIndicator = document.createElement('div');
    loadingIndicator.id = 'blog-loading-indicator';
    loadingIndicator.className = 'text-center py-5';
    loadingIndicator.innerHTML = `
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
      <p class="mt-2 mb-0">Filtering articles...</p>
    `;
    
    this.articlesContainer.appendChild(loadingIndicator);
  }

  hideLoadingState() {
    this.articlesContainer.style.opacity = '1';
    this.articlesContainer.style.pointerEvents = 'auto';
    
    const loadingIndicator = document.getElementById('blog-loading-indicator');
    if (loadingIndicator) {
      loadingIndicator.remove();
    }
  }

  handleError(error) {
    // Log error for debugging in development
    if (window.location.hostname === 'localhost' || window.location.hostname.includes('myshopify.com')) {
      console.error('Blog filter error:', error);
    }
    
    this.showErrorState();
  }

  showErrorState() {
    // Remove any existing error messages
    const existingError = this.articlesContainer.querySelector('.blog-filter-error');
    if (existingError) existingError.remove();
    
    const errorIndicator = document.createElement('div');
    errorIndicator.className = 'alert alert-warning text-center blog-filter-error';
    errorIndicator.innerHTML = `
      <p class="mb-0">Sorry, there was an error loading the filtered results. Please try again.</p>
    `;
    
    this.articlesContainer.appendChild(errorIndicator);
    
    // Remove error after 5 seconds
    setTimeout(() => {
      if (errorIndicator.parentNode) {
        errorIndicator.remove();
      }
    }, 5000);
  }

  reinitializeLazyLoading() {
    // Reinitialize the lazy loading observer for new images
    if (typeof lazyImageObserver !== 'undefined' && lazyImageObserver) {
      lazyImageObserver.observe();
    } else if (typeof lozad !== 'undefined') {
      // If lozad is available but observer isn't initialized, create it
      window.lazyImageObserver = lozad('.lozad', {
        rootMargin: '50px 0px',
        enableAutoReload: true
      });
      window.lazyImageObserver.observe();
    }

    // Force load any images that don't have the lozad class but have data-src
    const lazyImages = this.articlesContainer.querySelectorAll('img[data-src]:not(.lozad)');
    lazyImages.forEach(img => {
      if (img.dataset.src) {
        img.src = img.dataset.src;
        img.removeAttribute('data-src');
      }
    });

    // Handle images that are already in viewport
    const allImages = this.articlesContainer.querySelectorAll('img.lozad');
    allImages.forEach(img => {
      const rect = img.getBoundingClientRect();
      if (rect.top < window.innerHeight + 50 && rect.bottom > -50) {
        if (img.dataset.src) {
          img.src = img.dataset.src;
          img.removeAttribute('data-src');
          img.classList.remove('lozad');
        }
      }
    });
  }
}

// Initialize blog filters when DOM is loaded
let blogFiltersInstance = null;

function initializeBlogFilters() {
  if (document.getElementById('blog-filter-form') && document.getElementById('blog-articles-container')) {
    blogFiltersInstance = new BlogFilters();
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeBlogFilters);
} else {
  initializeBlogFilters();
}

// Handle browser back/forward buttons
window.addEventListener('popstate', (event) => {
  if (event.state && blogFiltersInstance) {
    if (event.state.categories && event.state.tags) {
      // Handle multiple selections
      blogFiltersInstance.filterArticlesMultiple(event.state.categories, event.state.tags);
    } else {
      // Handle single selections (backward compatibility)
      blogFiltersInstance.filterArticles(event.state.category || '', event.state.tag || '');
    }
  }
});
